import type { HudEdge } from '@sorrem/shared';
import { resolveEssenceExchangeValue, getStoredLoadout, resolveCommissionReservedEssence, resolveSpendableCraftEssence } from '@sorrem/shared';
import type { RecipeDefinition } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { CarriedPanel, CarriedSlotContext } from '@sorrem-client/ui/components';
import { useGameSelector } from '@sorrem-client/ui/hooks';
import { useMemo } from 'react';

interface RunCarriedPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSlotIndex: number | null;
    onSelectedSlotChange: (slotIndex: number | null) => void;
    assigningSlotIndex: number | null;
    onAssigningSlotChange: (slotIndex: number | null) => void;
    /** Equips a carried piece of equipment, swapping out what it replaces. */
    onEquip: (item: UIInventoryItem) => void;
    /** The screen edge it slides off: the side of its HUD area (#210). */
    edge?: HudEdge;
}

/**
 * The in-run Carried panel, fed from the running game. The selected slot
 * shows the shared `CarriedSlotContext`: coalescing spends run essence first,
 * except what the run's commission holds for its gather goals (#236), and the
 * player's banked essence (their vessel) after it. The run and
 * banked essence themselves show in the loadout's Essence Vessel.
 */
export function RunCarriedPanel({
    edge = 'left',
    open,
    onOpenChange,
    selectedSlotIndex,
    onSelectedSlotChange,
    assigningSlotIndex,
    onAssigningSlotChange,
    onEquip,
}: RunCarriedPanelProps): JSX.Element {
    const { bridge, requestStartCraft, requestCancelCraft, requestConsumeCrystal, requestDropItem } = useGameplay();
    const carriedItems = useGameSelector(bridge, (state) => state.player.carriedItems);
    const totalSlots = useGameSelector(bridge, (state) => state.player.maxCarryCapacity);
    // How equipment compares with what is worn (#293, #341), marked with chevrons.
    const itemComparisons = useGameSelector(bridge, (state) => state.player.itemComparisons);
    // The run publishes the loadout already as wielded (see RunUiPublisher), so it is read as it is.
    const equipped = useGameSelector(bridge, (state) => getStoredLoadout(state.player));
    const essencePool = useGameSelector(bridge, (state) => state.player.essencePool);
    const bankedEssencePool = useGameSelector(bridge, (state) => state.player.bankedEssencePool);
    const coalescences = useGameSelector(bridge, (state) => state.coalescences);
    const commission = useGameSelector(bridge, (state) => state.commission);

    const heldEssence = useMemo(
        () => (commission === null ? {} : resolveCommissionReservedEssence(commission.goals, essencePool)),
        [commission, essencePool],
    );
    const spendableEssence = useMemo(
        () => resolveSpendableCraftEssence(essencePool, heldEssence, bankedEssencePool),
        [bankedEssencePool, essencePool, heldEssence],
    );
    const contextSlotIndex = selectedSlotIndex !== null && selectedSlotIndex < totalSlots ? selectedSlotIndex : null;
    const contextPending = useMemo(() => coalescences
        .filter((coalescence) => coalescence.targetSlotIndex === contextSlotIndex)
        .sort((a, b) => a.completesAtMs - b.completesAtMs), [coalescences, contextSlotIndex]);

    const handleStart = async (slotIndex: number, recipe: RecipeDefinition): Promise<string | null> => {
        const result = await requestStartCraft(slotIndex, recipe.id);
        return result.ok ? null : result.reason;
    };

    return (
        <CarriedPanel
            itemComparisons={itemComparisons}
            edge={edge}
            open={open}
            onOpenChange={onOpenChange}
            carriedItems={carriedItems}
            totalSlots={totalSlots}
            coalescences={coalescences}
            selectedSlotIndex={selectedSlotIndex}
            onSelectedSlotChange={onSelectedSlotChange}
            assigningSlotIndex={assigningSlotIndex}
            onAssigningSlotChange={onAssigningSlotChange}
            context={contextSlotIndex === null ? null : (
                <CarriedSlotContext
                    slotIndex={contextSlotIndex}
                    item={carriedItems[contextSlotIndex]}
                    pending={contextPending}
                    essencePool={spendableEssence}
                    heldEssenceValue={resolveEssenceExchangeValue(heldEssence)}
                    onStartCoalesce={(recipe) => handleStart(contextSlotIndex, recipe)}
                    onCancelCoalesce={requestCancelCraft}
                    crystalAction={{ label: 'Use', onClick: (item) => requestConsumeCrystal(item.id) }}
                    equipped={equipped}
                    onEquip={onEquip}
                    onDrop={(item) => {
                        requestDropItem(item.id);
                        onSelectedSlotChange(null);
                    }}
                />
            )}
        />
    );
}
